import type { Theme } from '../providers/ThemeProvider';

// Shared cookie name with @commander/theme-changer for cross-app sync
export const THEME_COOKIE_NAME = 'commander-theme-mode';

// Cookie format uses API-style values
type CookieThemeMode = 'LIGHT_MODE' | 'DARK_MODE' | 'SYSTEM';

/**
 * Convert UI theme format to cookie format
 */
function toCookieFormat(theme: Theme): CookieThemeMode {
  switch (theme) {
    case 'light':
      return 'LIGHT_MODE';
    case 'dark':
      return 'DARK_MODE';
    case 'system':
      return 'SYSTEM';
    default:
      return 'LIGHT_MODE';
  }
}

/**
 * Convert cookie format to UI theme format
 */
function fromCookieFormat(mode: CookieThemeMode): Theme {
  switch (mode) {
    case 'LIGHT_MODE':
      return 'light';
    case 'DARK_MODE':
      return 'dark';
    case 'SYSTEM':
      return 'system';
    default:
      return 'light';
  }
}

/**
 * Set a cookie that works across all ports on localhost
 */
function setThemeCookie(theme: Theme): void {
  const hostname = window.location.hostname;
  const cookieValue = toCookieFormat(theme);
  const secure = window.location.protocol === 'https:' ? ';Secure' : '';

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // For localhost: NO domain attribute - allows sharing across all ports
    document.cookie = `${THEME_COOKIE_NAME}=${cookieValue};path=/;max-age=31536000;SameSite=Lax${secure}`;
  } else {
    // For production: use dot-prefixed domain for subdomain sharing
    const domain = hostname.startsWith('www.') ? hostname.substring(4) : hostname;
    document.cookie = `${THEME_COOKIE_NAME}=${cookieValue};path=/;domain=.${domain};max-age=31536000;SameSite=Lax${secure}`;
  }
}

/**
 * Get theme from cookie
 */
function getThemeCookie(): Theme | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${THEME_COOKIE_NAME}=([^;]+)`));
  if (match) {
    const value = match[1];
    if (value === 'LIGHT_MODE' || value === 'DARK_MODE' || value === 'SYSTEM') {
      return fromCookieFormat(value as CookieThemeMode);
    }
  }
  return null;
}

/**
 * Sync theme across all Commander apps via cookie
 */
export function syncThemeAcrossApps(theme: Theme): void {
  setThemeCookie(theme);
}

/**
 * Get the global theme setting from cookie
 */
export function getGlobalTheme(): Theme {
  return getThemeCookie() || 'system';
}

/**
 * Initialize theme on app startup from cookie
 */
export function initializeThemeSync(currentTheme: Theme): Theme {
  const globalTheme = getGlobalTheme();

  if (globalTheme !== 'system') {
    return globalTheme;
  }

  return currentTheme;
}
